import { Check, Circle, X, Clock } from 'lucide-react'
import type { RunStatus } from '@/lib/types'
import { cn } from '@/lib/utils'

export interface RunTimelineProps {
  /** Current run status */
  status: RunStatus
  /** Timestamps for each phase */
  timestamps: {
    created?: string
    queued?: string
    started?: string
    finished?: string
  }
  /** Visual variant */
  variant?: 'default' | 'compact'
  /** Additional CSS classes */
  className?: string
}

type TimelineStep = {
  key: string
  label: string
  timestamp?: string
  state: 'completed' | 'current' | 'pending' | 'failed'
}

function getTimelineSteps(
  status: RunStatus,
  timestamps: RunTimelineProps['timestamps']
): TimelineStep[] {
  const steps: TimelineStep[] = [
    {
      key: 'pending',
      label: 'PENDING',
      timestamp: timestamps.created,
      state: 'completed',
    },
    {
      key: 'queued',
      label: 'QUEUED',
      timestamp: timestamps.queued,
      state: 'pending',
    },
    {
      key: 'running',
      label: 'RUNNING',
      timestamp: timestamps.started,
      state: 'pending',
    },
    {
      key: 'terminal',
      label: 'SUCCEEDED',
      timestamp: timestamps.finished,
      state: 'pending',
    },
  ]

  // Update states based on current status
  switch (status) {
    case 'PENDING':
      steps[0].state = 'current'
      break
    case 'QUEUED':
      steps[0].state = 'completed'
      steps[1].state = 'current'
      break
    case 'RUNNING':
      steps[0].state = 'completed'
      steps[1].state = 'completed'
      steps[2].state = 'current'
      break
    case 'SUCCEEDED':
      steps[0].state = 'completed'
      steps[1].state = 'completed'
      steps[2].state = 'completed'
      steps[3].state = 'completed'
      steps[3].label = 'SUCCEEDED'
      break
    case 'FAILED':
      steps[0].state = 'completed'
      steps[1].state = 'completed'
      steps[2].state = 'completed'
      steps[3].state = 'failed'
      steps[3].label = 'FAILED'
      break
    case 'CANCELLED':
      steps[0].state = 'completed'
      steps[1].state = timestamps.queued ? 'completed' : 'pending'
      steps[2].state = timestamps.started ? 'completed' : 'pending'
      steps[3].state = 'failed'
      steps[3].label = 'CANCELLED'
      break
    case 'TIMEOUT':
      steps[0].state = 'completed'
      steps[1].state = 'completed'
      steps[2].state = 'completed'
      steps[3].state = 'failed'
      steps[3].label = 'TIMEOUT'
      break
  }

  return steps
}

function formatTimestamp(timestamp?: string): string {
  if (!timestamp) return '(pending)'
  const date = new Date(timestamp)
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function StepIcon({ state }: { state: TimelineStep['state'] }) {
  switch (state) {
    case 'completed':
      return (
        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
          <Check className="w-4 h-4 text-white" />
        </div>
      )
    case 'current':
      return (
        <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center animate-pulse">
          <Circle className="w-3 h-3 text-white fill-white" />
        </div>
      )
    case 'failed':
      return (
        <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
          <X className="w-4 h-4 text-white" />
        </div>
      )
    case 'pending':
    default:
      return (
        <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center">
          <Clock className="w-3 h-3 text-gray-400" />
        </div>
      )
  }
}

/**
 * Visual step progress indicator showing run phases.
 * 
 * @example
 * ```tsx
 * <RunTimeline
 *   status="RUNNING"
 *   timestamps={{
 *     created: "2025-12-19T14:28:30Z",
 *     queued: "2025-12-19T14:28:32Z",
 *     started: "2025-12-19T14:28:45Z",
 *   }}
 * />
 * ```
 */
export function RunTimeline({
  status,
  timestamps,
  variant = 'default',
  className,
}: RunTimelineProps) {
  const steps = getTimelineSteps(status, timestamps)

  if (variant === 'compact') {
    return (
      <div className={cn('flex items-center gap-1', className)}>
        {steps.map((step, index) => (
          <div key={step.key} className="flex items-center">
            <div
              className={cn(
                'w-2 h-2 rounded-full',
                step.state === 'completed' && 'bg-green-500',
                step.state === 'current' && 'bg-primary animate-pulse',
                step.state === 'failed' && 'bg-red-500',
                step.state === 'pending' && 'bg-gray-300'
              )}
            />
            {index < steps.length - 1 && (
              <div
                className={cn(
                  'w-4 h-0.5',
                  steps[index + 1].state !== 'pending'
                    ? 'bg-green-500'
                    : 'bg-gray-300'
                )}
              />
            )}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={cn('py-4', className)}>
      <div className="flex items-center">
        {steps.map((step, index) => (
          <div key={step.key} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <StepIcon state={step.state} />
              <span className="mt-2 text-xs font-medium text-muted-foreground">
                {step.label}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatTimestamp(step.timestamp)}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={cn(
                  'flex-1 h-0.5 mx-2',
                  steps[index + 1].state !== 'pending'
                    ? 'bg-green-500'
                    : 'bg-gray-200'
                )}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
